"use server";

import { auth, currentUser } from "@clerk/nextjs/server";
import Stripe from "stripe";
import { client, writeClient } from "@/sanity/lib/client";
import { PRODUCTS_BY_IDS_QUERY } from "@/sanity/queries/products";
import { ORDER_BY_STRIPE_PAYMENT_ID_QUERY } from "@/sanity/queries/orders";
import { getOrCreateStripeCustomer } from "@/lib/actions/customer";
import { sendOrderConfirmationEmail } from "@/lib/email";

if (!process.env.STRIPE_SECRET_KEY) {
  throw new Error("STRIPE_SECRET_KEY is not defined");
}

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY, {
  apiVersion: "2026-02-25.clover",
});

// Types
interface SelectedOption {
  groupId: string;
  groupName: string;
  optionValue: string;
  optionName: string;
  priceModifier: number;
}

interface CartItem {
  cartItemId: string;
  productId: string;
  name: string;
  basePrice: number;
  price: number; // includes priceModifiers
  quantity: number;
  image?: string;
  selectedOptions: SelectedOption[];
}

interface CheckoutResult {
  success: boolean;
  url?: string;
  error?: string;
}

/**
 * Creates a Stripe Checkout Session from cart items
 * Validates stock and prices against Sanity before creating session
 */
export async function createCheckoutSession(
  items: CartItem[],
  locale: "en-US" | "pt-PT" = "en-US"
): Promise<CheckoutResult> {
  try {
    // 1. Check auth (optional — guest checkout is allowed)
    const { userId } = await auth();
    const user = userId ? await currentUser() : null;

    // 2. Validate cart is not empty
    if (!items || items.length === 0) {
      return { success: false, error: "Your cart is empty" };
    }

    // 3. Fetch current product data from Sanity to validate prices/stock
    const productIds = items.map((item) => item.productId);
    const products = await client.fetch(PRODUCTS_BY_IDS_QUERY, {
      ids: productIds,
      locale: "en-US",
    });

    // 4. Validate each item
    const validationErrors: string[] = [];
    const validatedItems: {
      product: (typeof products)[number];
      quantity: number;
      cartItem: CartItem;
    }[] = [];

    for (const item of items) {
      const product = products.find(
        (p: { _id: string }) => p._id === item.productId
      );

      if (!product) {
        validationErrors.push(`Product "${item.name}" is no longer available`);
        continue;
      }

      if ((product.stock ?? 0) === 0) {
        validationErrors.push(`"${product.name}" is out of stock`);
        continue;
      }

      if (item.quantity > (product.stock ?? 0)) {
        validationErrors.push(
          `Only ${product.stock} of "${product.name}" available`
        );
        continue;
      }

      validatedItems.push({ product, quantity: item.quantity, cartItem: item });
    }

    if (validationErrors.length > 0) {
      return { success: false, error: validationErrors.join(". ") };
    }

    // 5. Create Stripe line items with validated prices
    const lineItems: Stripe.Checkout.SessionCreateParams.LineItem[] =
      validatedItems.map(({ product, quantity, cartItem }) => {
        const optionsSummary = (cartItem.selectedOptions?.length ?? 0) > 0
          ? cartItem.selectedOptions
              .map((o) => `${o.groupName}: ${o.optionName}`)
              .join(" | ")
          : undefined;

        return {
          price_data: {
            currency: "eur",
            product_data: {
              name: product.name ?? "Product",
              description: optionsSummary,
              images: product.image?.asset?.url
                ? [product.image.asset.url]
                : [],
              metadata: {
                productId: product._id,
                cartItemId: cartItem.cartItemId,
              },
            },
            // Use client price (includes customization modifiers)
            unit_amount: Math.round(cartItem.price * 100),
          },
          quantity,
        };
      });

    // 6. Get or create Stripe customer (logged-in users only)
    let stripeCustomerId: string | undefined;
    let sanityCustomerId = "";
    let userEmail = "";

    if (user && userId) {
      userEmail = user.emailAddresses[0]?.emailAddress ?? "";
      const userName = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || userEmail;
      const customerResult = await getOrCreateStripeCustomer(userEmail, userName, userId);
      stripeCustomerId = customerResult.stripeCustomerId;
      sanityCustomerId = customerResult.sanityCustomerId;
    }

    // 7. Prepare metadata for webhook
    // selectedOptions per item stored as compact JSON: [{g, o, p}] = groupName, optionName, priceModifier
    const optionsMetadata: Record<string, string> = {};
    validatedItems.forEach(({ cartItem }, index) => {
      if (cartItem.selectedOptions?.length > 0) {
        optionsMetadata[`options_${index}`] = JSON.stringify(
          cartItem.selectedOptions.map((o) => ({
            g: o.groupName,
            o: o.optionName,
            p: o.priceModifier,
          }))
        );
      }
    });

    const metadata: Record<string, string> = {
      ...(userId && { clerkUserId: userId }),
      ...(userEmail && { userEmail }),
      ...(sanityCustomerId && { sanityCustomerId }),
      locale,
      productIds: validatedItems.map((i) => i.product._id).join(","),
      quantities: validatedItems.map((i) => i.quantity).join(","),
      cartItemIds: validatedItems.map((i) => i.cartItem.cartItemId).join(","),
      ...optionsMetadata,
    };

    // 8. Create Stripe Checkout Session
    // Priority: NEXT_PUBLIC_BASE_URL > Vercel URL > localhost
    const baseUrl =
      process.env.NEXT_PUBLIC_BASE_URL ||
      (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null) ||
      "http://localhost:3000";

    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      payment_method_types: ["card"],
      line_items: lineItems,
      ...(stripeCustomerId ? { customer: stripeCustomerId } : {}),
      shipping_address_collection: {
        allowed_countries: [
          "GB", // United Kingdom
          "US", // United States
          "CA", // Canada
          "AU", // Australia
          "NZ", // New Zealand
          "IE", // Ireland
          "DE", // Germany
          "FR", // France
          "ES", // Spain
          "IT", // Italy
          "NL", // Netherlands
          "BE", // Belgium
          "AT", // Austria
          "CH", // Switzerland
          "SE", // Sweden
          "NO", // Norway
          "DK", // Denmark
          "FI", // Finland
          "PT", // Portugal
          "PL", // Poland
          "CZ", // Czech Republic
          "GR", // Greece
          "HU", // Hungary
          "RO", // Romania
          "BG", // Bulgaria
          "HR", // Croatia
          "SI", // Slovenia
          "SK", // Slovakia
          "LT", // Lithuania
          "LV", // Latvia
          "EE", // Estonia
          "LU", // Luxembourg
          "MT", // Malta
          "CY", // Cyprus
          "JP", // Japan
          "SG", // Singapore
          "HK", // Hong Kong
          "KR", // South Korea
          "TW", // Taiwan
          "MY", // Malaysia
          "TH", // Thailand
          "IN", // India
          "AE", // United Arab Emirates
          "SA", // Saudi Arabia
          "IL", // Israel
          "ZA", // South Africa
          "BR", // Brazil
          "MX", // Mexico
          "AR", // Argentina
          "CL", // Chile
          "CO", // Colombia
        ],
      },
      metadata,
      success_url: `${baseUrl}/checkout/success?session_id={CHECKOUT_SESSION_ID}&lang=${locale}`,
      cancel_url: `${baseUrl}/checkout?lang=${locale}`,
    });

    return { success: true, url: session.url ?? undefined };
  } catch (error) {
    console.error("Checkout error:", error);
    return {
      success: false,
      error: "Something went wrong. Please try again.",
    };
  }
}

/**
 * Retrieves a checkout session by ID (for success page)
 */
export async function getCheckoutSession(sessionId: string) {
  try {
    const { userId } = await auth();

    const session = await stripe.checkout.sessions.retrieve(sessionId, {
      expand: ["line_items", "customer_details"],
    });

    // For logged-in users, verify the session belongs to them
    const sessionUserId = session.metadata?.clerkUserId;
    if (sessionUserId && userId && sessionUserId !== userId) {
      return { success: false, error: "Session not found" };
    }

    return {
      success: true,
      session: {
        id: session.id,
        customerEmail: session.customer_details?.email,
        customerName: session.customer_details?.name,
        amountTotal: session.amount_total,
        paymentStatus: session.payment_status,
        shippingAddress: session.customer_details?.address,
        lineItems: session.line_items?.data.map((item) => ({
          name: item.description,
          quantity: item.quantity,
          amount: item.amount_total,
        })),
      },
    };
  } catch (error) {
    console.error("Get session error:", error);
    return { success: false, error: "Could not retrieve order details" };
  }
}

/**
 * Fallback: ensures an order exists in Sanity for a completed Stripe session.
 * Called from the success page so orders are always created even if the webhook
 * was delayed or never delivered (e.g. local dev without stripe listen).
 */
export async function ensureOrderFromSession(sessionId: string): Promise<{ success: boolean; orderId?: string; orderNumber?: string; isGuest?: boolean; error?: string }> {
  try {
    const { userId } = await auth();

    const session = await stripe.checkout.sessions.retrieve(sessionId, {
      expand: ["line_items.data.price.product"],
    });

    // Verify session ownership: must match logged-in user OR be a guest session
    const sessionUserId = session.metadata?.clerkUserId;
    if (sessionUserId && userId && sessionUserId !== userId) {
      return { success: false, error: "Session not found" };
    }

    if (session.payment_status !== "paid") {
      return { success: false, error: "Payment not completed" };
    }

    const isGuest = !sessionUserId;

    const paymentIntentId = session.payment_intent as string;

    // Idempotency: return existing order if webhook already created it
    const existing = await writeClient.fetch(ORDER_BY_STRIPE_PAYMENT_ID_QUERY, {
      stripePaymentId: paymentIntentId,
    });
    if (existing?._id) return { success: true, orderId: existing._id, orderNumber: existing.orderNumber, isGuest };

    const { metadata } = session;
    if (!metadata?.productIds || !metadata?.quantities) {
      return { success: false, error: "Session metadata missing" };
    }

    const productIds = metadata.productIds.split(",");
    const quantities = metadata.quantities.split(",").map(Number);
    const orderNumber = `ORD-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;

    const items = productIds.map((productId, index) => {
      const lineItem = session.line_items?.data[index];
      const rawOptions = metadata[`options_${index}`];
      const selectedOptions = rawOptions
        ? (JSON.parse(rawOptions) as { g: string; o: string; p: number }[]).map(
            (opt) => ({
              _key: `opt_${Math.random().toString(36).slice(2, 7)}`,
              groupName: opt.g,
              optionName: opt.o,
              priceModifier: opt.p,
            })
          )
        : [];
      return {
        _key: `item_${index}_${Math.random().toString(36).slice(2, 8)}`,
        product: { _type: "reference", _ref: productId },
        quantity: quantities[index] ?? 1,
        priceAtPurchase: lineItem?.price?.unit_amount ? lineItem.price.unit_amount / 100 : 0,
        ...(selectedOptions.length > 0 && { selectedOptions }),
      };
    });

    const shippingAddress = session.customer_details?.address;
    const address = shippingAddress ? {
      name: session.customer_details?.name ?? "",
      line1: shippingAddress.line1 ?? "",
      line2: shippingAddress.line2 ?? "",
      city: shippingAddress.city ?? "",
      postcode: shippingAddress.postal_code ?? "",
      country: shippingAddress.country ?? "",
    } : undefined;

    const orderDoc = {
      _type: "order",
      orderNumber,
      items,
      total: session.amount_total ? session.amount_total / 100 : 0,
      status: "paid",
      ...(sessionUserId && { clerkUserId: sessionUserId }),
      email: session.customer_details?.email ?? metadata.userEmail ?? "",
      ...(address && { address }),
      stripePaymentId: paymentIntentId,
      createdAt: new Date().toISOString(),
      ...(metadata.sanityCustomerId && {
        customer: { _type: "reference", _ref: metadata.sanityCustomerId },
      }),
    };

    const result = await writeClient.create(orderDoc);

    // Send order confirmation email
    const customerEmail = session.customer_details?.email ?? metadata.userEmail ?? "";
    if (customerEmail) {
      const baseUrl =
        process.env.NEXT_PUBLIC_BASE_URL ||
        (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : null) ||
        "http://localhost:3000";
      const emailLocale = (metadata.locale as "en-US" | "pt-PT") ?? "en-US";
      const trackUrl = `${baseUrl}/track?code=${orderNumber}&email=${encodeURIComponent(customerEmail)}`;

      await sendOrderConfirmationEmail({
        to: customerEmail,
        orderNumber,
        total: orderDoc.total,
        locale: emailLocale,
        items: items.map((item, i) => ({
          name: session.line_items?.data[i]?.description ?? "Product",
          quantity: item.quantity,
          priceAtPurchase: item.priceAtPurchase,
          selectedOptions: (item as { selectedOptions?: { groupName: string; optionName: string }[] }).selectedOptions,
        })),
        address,
        trackUrl,
      }).catch((err) => console.error("Failed to send confirmation email:", err));
    }

    return { success: true, orderId: result._id, orderNumber, isGuest };
  } catch (error) {
    console.error("ensureOrderFromSession error:", error);
    return { success: false, error: "Could not create order" };
  }
}
